import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import { getLocationLevelLabel } from "api-types/locations/locationDisplay";
import { isUnarchiveNameConflictError, isUnarchiveSelfNameConflictError } from "common/types/structuredErrorResponse";
import { formatDate } from "common/utils/dates";
import AdminModal from "lib/components/admin/builders/AdminModal";
import { getBuilderLocationsURL } from "lib/endpointHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import { putLocation } from "lib/storage/locationStorage";
import type { FormEvent } from "react";
import { useState } from "react";
import { useSWRConfig } from "swr";
import InputGroupText from "ui/forms/InputGroupText";
import useAppContext from "ui/hooks/useAppContext";

interface UnarchiveLocationModalProps {
  location: ClientLocationWithLevel;
  conflictingName: string;
  onClose: () => void;
  onSuccess: (location: ClientLocationWithLevel) => void;
}

/** Renames and unarchives in one request, for when an active sibling holds the name. */
export default function UnarchiveLocationModal({
  location,
  conflictingName: initialConflictingName,
  onClose,
  onSuccess,
}: UnarchiveLocationModalProps) {
  const { setUserNotification } = useAppContext();
  const { builder, hasPermission } = useUserContext();
  const { mutate } = useSWRConfig();
  const [name, setName] = useState(location.name);
  const [conflictingName, setConflictingName] = useState<string | null>(initialConflictingName);
  const [descendantConflictMessage, setDescendantConflictMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // The combined rename+unarchive needs EDIT_COMMUNITIES on top of the ARCHIVE that got them here.
  const canRename = hasPermission("EDIT_COMMUNITIES", { resourceId: location.id });
  const locationLevel = getLocationLevelLabel(location, builder?.locationSettings);
  const archivedDate = location.archivedAt ? formatDate(new Date(location.archivedAt)) : null;
  const conflictPlace = location.parentId === null ? "at the top level" : "under the same parent";

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    const trimmedName = name.trim();
    setIsSaving(true);
    const [updatedLocation, err] = await putLocation(location.builderId, location.id, {
      archivedAt: null,
      name: trimmedName,
    });

    if (err) {
      // Stay open to retry, tracking whichever name the server reports as taken. A descendant
      // conflict means the rename was fine and the fix lives on other locations.
      if (isUnarchiveSelfNameConflictError(err.data)) {
        setConflictingName(err.data.conflictingName);
        setDescendantConflictMessage(null);
      } else if (isUnarchiveNameConflictError(err.data)) {
        setConflictingName(null);
        setDescendantConflictMessage(err.message);
      }
      setUserNotification(err);
      setIsSaving(false);
      return;
    }

    setUserNotification({
      messageType: "success",
      message: `${locationLevel} has been unarchived as "${trimmedName}".`,
    });
    onSuccess(updatedLocation);

    // isSaving stays true through this so the submit button cannot re-fire while it settles.
    await mutate((key: string) => {
      const baseUrl = getBuilderLocationsURL({ builderId: location.builderId });
      return typeof key === "string" && key.includes(baseUrl);
    });
    onClose();
  };

  const trimmedName = name.trim();
  const submitDisabled = isSaving || !canRename || !trimmedName || trimmedName === conflictingName;

  return (
    <AdminModal
      headerText={`Unarchive ${locationLevel}`}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitButtonText="Rename & Unarchive"
      disabled={submitDisabled}
    >
      {conflictingName !== null ? (
        <p>
          An active {locationLevel} named &quot;{conflictingName}&quot; already exists {conflictPlace}. Rename this{" "}
          {locationLevel}
          {archivedDate ? ` (archived ${archivedDate})` : ""} to unarchive it.
        </p>
      ) : (
        <p>{descendantConflictMessage}</p>
      )}
      {canRename ? (
        <InputGroupText
          type="text"
          name="name"
          labelText={`${locationLevel} Name`}
          inputValue={name}
          onChange={setName}
          required
        />
      ) : (
        <p>
          Renaming requires edit access to this {locationLevel}, which your account does not have. Ask someone with edit
          access to rename either location, then unarchive again.
        </p>
      )}
    </AdminModal>
  );
}
